import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { useLanguage } from "@/hooks/useLanguage";
import { logger } from "@/lib/logger";

// Declare the adsbygoogle global for TypeScript
declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

const LoadingWithAdsSection = () => {
  const [messageIndex, setMessageIndex] = useState(0);
  const { language, t } = useLanguage();
  const adTopRef = useRef<HTMLDivElement>(null);
  const adBottomRef = useRef<HTMLDivElement>(null);
  const [adsInitialized, setAdsInitialized] = useState(false);
  
  const loadingMessages = [
    `✨ ${t('loadingMessages.craftingTheNextChapter')}...`,
    `📖 ${t('loadingMessages.weavingThePlotTwists')}...`,
    `🚀 ${t('loadingMessages.yourAdventureIsAboutToUnfold')}...`,
    `🎭 ${t('loadingMessages.developingTheCharacters')}...`,
    `🌟 ${t('loadingMessages.imaginingNewPossibilities')}...`,
  ];

  // Setup Google AdSense script
  useEffect(() => {
    const loadAdScript = () => {
      const script = document.createElement('script');
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6644109320388531';
      script.async = true;
      script.crossOrigin = 'anonymous';
      
      // Wait for script to load before initializing ads
      script.onload = () => {
        setAdsInitialized(true);
      };
      
      document.head.appendChild(script);
    };
    
    // Check if the script is already loaded to avoid duplicates
    if (!document.querySelector('script[src*="adsbygoogle.js"]')) {
      loadAdScript();
    } else {
      // Script already exists, so we can initialize ads
      setAdsInitialized(true);
    }
  }, []);
  
  // Initialize ads after component is fully mounted and visible
  useEffect(() => {
    // Only proceed if the component is mounted, visible, and the script is loaded
    if (!adsInitialized) return;
    
    // Use a delay to ensure the DOM has properly rendered with correct sizes
    const initializeAdsTimeout = setTimeout(() => {
      try {
        // Initialize each ad unit separately
        if (adTopRef.current) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
        
        if (adBottomRef.current) {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
      } catch (e) {
        logger.error('AdSense initialization error:', e);
      }
    }, 500); // Half a second delay to ensure the DOM is ready
    
    return () => clearTimeout(initializeAdsTimeout);
  }, [adsInitialized]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [loadingMessages.length]);

  return (
    <div className="w-full space-y-8">
      {/* Ad Banner above loading animation - fixed dimensions to prevent the error */}
      <div className="w-full flex justify-center mb-4">
        <div 
          ref={adTopRef}
          className="w-full min-h-[100px]" // Ensure minimum height for the ad container
        >
          <ins className="adsbygoogle"
            style={{ 
              display: 'block',
              minHeight: '100px', 
              minWidth: '300px',
              width: '100%'
            }}
            data-ad-client="ca-pub-6644109320388531"
            data-ad-slot="3257404489"
            data-ad-format="auto"
            data-full-width-responsive="true">
          </ins>
        </div>
      </div>

      <Card className="mb-8 bg-white p-8 shadow-lg">
        <div className="prose mx-auto max-w-3xl">
          <div className="space-y-4">
            {/* Animated text placeholder */}
            <motion.div
              initial={{ opacity: 0.3 }}
              animate={{ opacity: 0.7 }}
              transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse" }}
              className="h-4 bg-gray-200 rounded w-3/4"
            />
            <motion.div
              initial={{ opacity: 0.3 }}
              animate={{ opacity: 0.7 }}
              transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse", delay: 0.2 }}
              className="h-4 bg-gray-200 rounded w-full"
            />
            <motion.div
              initial={{ opacity: 0.3 }}
              animate={{ opacity: 0.7 }}
              transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse", delay: 0.4 }}
              className="h-4 bg-gray-200 rounded w-5/6"
            />
          </div>

          {/* Loading message */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            key={messageIndex}
            className="mt-8 text-center text-primary font-medium"
          >
            {loadingMessages[messageIndex]}
          </motion.p>

          {/* AI suggestions placeholders */}
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0.3 }}
                animate={{ opacity: 0.7 }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: i * 0.2,
                }}
                className="h-24 bg-gray-100 rounded-lg border-2 border-gray-200"
              />
            ))}
          </div>
        </div>
      </Card>

      {/* Ad Banner below loading animation - fixed dimensions to prevent the error */}
      <div className="w-full flex justify-center mt-4">
        <div 
          ref={adBottomRef}
          className="w-full min-h-[100px]" // Ensure minimum height for the ad container
        >
          <ins className="adsbygoogle"
            style={{ 
              display: 'block', 
              minHeight: '100px',
              minWidth: '300px',
              width: '100%'
            }}
            data-ad-client="ca-pub-6644109320388531"
            data-ad-slot="6651097858"
            data-ad-format="auto"
            data-full-width-responsive="true">
          </ins>
        </div>
      </div>
    </div>
  );
};

export default LoadingWithAdsSection;